import { z } from 'zod';
import { FiAnchor, FiCalendar } from 'react-icons/fi';
import {
  TbBoxMultiple,
  TbBracketsContainEnd,
  TbBracketsContainStart,
  TbColumns3,
  TbDecimal,
  TbEqual,
  TbLogicAnd,
  TbLogicOr,
  TbMathFunction,
  TbMinus,
  TbNumbers,
  TbPlus,
  TbTable,
  TbTableAlias,
  TbX,
  TbMathGreater,
  TbMathEqualGreater,
  TbMathEqualLower,
  TbMathLower,
  TbExclamationMark,
  TbVariable,
} from 'react-icons/tb';
import { TfiSplitH } from 'react-icons/tfi';
import { CgMathDivide } from 'react-icons/cg';
import { LuEraser, LuReplaceAll, LuSplit, LuGitBranch } from 'react-icons/lu';
import { DiMongodb } from 'react-icons/di';
import { VscSymbolString } from 'react-icons/vsc';
import { AiOutlineMergeCells } from 'react-icons/ai';
import { MdOutlineStop, MdDataArray } from 'react-icons/md';
import { PiArrowUp, PiTextbox } from 'react-icons/pi';
import { BlockRule, GraphNode, block, typeLogicBlockRecord } from '../../types';
import { Input } from '@morph-mapper/node-inputs';
import {
  OptionType,
  PortDirection,
  reservedWrapVariables,
} from '@morph-mapper/types';
import { ReservedNodeType } from '@morph-mapper/types';
import { replaceEscapedCharacter } from '@morph-mapper/utils';

/**
 * FIXME: This function is a temporary fix to refit the absolute path to a local reference.
 * This function has several limitations and should be implicit in the context of the application later.
 * This function SHOULD be removed.
 *
 * - limit: absolute paths are now not available.
 * - limit: not aware of how paths work, may break if the path is a variable referencing another path.
 */
const replaceInObjRecursive = (
  obj: any,
  search: string,
  replace: (v: string) => string,
) => {
  if (typeof obj === 'string') {
    return replace(obj);
  }

  for (const key in obj) {
    if (typeof obj[key] === 'string' && obj[key].startsWith('$inputdocument')) {
      obj[key] = replace(obj[key]);
    } else if (typeof obj[key] === 'object') {
      replaceInObjRecursive(obj[key], search, replace);
    }
  }

  return obj;
};

const filterIncomingPathReference = (value: any, reference: string) => {
  const replacePathString = (v: string) => {
    return (
      '$data.' +
      v
        .split('.')
        .slice(reference.split('.').length - 1)
        .join('.')
    );
  };

  if (typeof value === 'object') {
    // Replace all strings which start with $inputdocument
    return replaceInObjRecursive(value, reference, replacePathString);
  }

  return replacePathString(value);
};

const LOGIC_BLOCKS_INTERNAL = typeLogicBlockRecord({
  terminal: block({
    title: 'Output',
    description: 'The output is the final result of your template.',
    category: 'internal',
    type: 'terminal',
    node: GraphNode.Terminal,
    icon: MdOutlineStop,
    mutation: ({ inputs }) => inputs.child,
    options: {},
    ports: {
      output: [],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.SingleChild],
  }),
  context: block({
    title: 'Context',
    description: 'Inject into the context of the variable.',
    category: 'internal',
    type: 'context',
    node: GraphNode.Terminal,
    icon: MdOutlineStop,
    mutation: ({ inputs }) => inputs.child,
    options: {},
    ports: {
      output: [],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.SingleChild],
  }),
});

const LOGIC_BLOCKS_CORE = typeLogicBlockRecord({
  anchor: block({
    title: 'Anchor',
    description:
      'Select text to act as a reference point of the value you want to extract.',
    category: 'core',
    type: 'anchor',
    node: GraphNode.Generic,
    icon: FiAnchor,
    mutation: ({ options, optionTypes }) => {
      const { iterate, skipWords, shift, selected, occurrence, whitespace } =
        options;
      const { selected: selectedType } = optionTypes;

      if (!selected) return;

      if (selectedType == OptionType.Reference) {
        return selected;
      }

      const renderIterate = () => {
        if (!iterate) return '';
        return iterate ? '>' : '';
      };
      const renderCount = () => {
        if (!shift) return '';
        return shift != 0 ? shift : '';
      };
      const renderSkipWords = () => {
        if (!skipWords) return '';
        return skipWords.length ? '|' + skipWords.join(',') : '';
      };
      const renderOccurrence = () => {
        if (!occurrence) return '';
        if (skipWords.length === 0) {
          // Skipwords does not render a pipe when undefined, so we need to add it here
          return occurrence > 0 ? `||${occurrence}` : '';
        }

        return occurrence > 0 ? `|${occurrence}` : '';
      };

      const anchor = `#${renderIterate()}${renderCount()}${renderSkipWords()}${renderOccurrence()}#${selected}`;

      if (whitespace) {
        return [[anchor]];
      } else {
        return anchor;
      }
    },
    options: {
      selected: {
        type: z.string(),
        title: 'Selector',
        description:
          'Select text from file which will act as a reference to extract data',
        input: Input.TextSelector,
        default: undefined,
        conditions: {},
      },
      iterate: {
        type: z.boolean(),
        title: 'Iterate',
        description: 'Use this variable with an iterator.',
        input: Input.Checkbox,
        default: false,
        conditions: {},
      },
      whitespace: {
        type: z.boolean(),
        title: 'Whitespace',
        description: 'Strip trailing whitespace in the output.',
        input: Input.Checkbox,
        default: false,
        conditions: {},
      },
      skipWords: {
        type: z.array(z.string()),
        title: 'Ignored words',
        description: 'Skip defined words and/or characters',
        input: Input.MultiSelectCreatable,
        default: [],
        conditions: {},
      },
      shift: {
        type: z.number(),
        title: 'Shift',
        description: 'Amount of words and/or characters to skip',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
      occurrence: {
        type: z.number(),
        title: 'Occurrence',
        description: 'Which occurrence of the anchor to use',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [],
    },
    rules: [BlockRule.Configurable, BlockRule.NoChildren],
  }),
  literal: block({
    title: 'Literal',
    description:
      'Add a literal value to your template. This can be a string, a number or a boolean.',
    category: 'core',
    type: 'literal',
    node: GraphNode.Generic,
    icon: VscSymbolString,
    mutation: ({ options }) => {
      const { literal } = options;

      if (literal == null) return '';
      if (typeof literal === 'string') {
        return replaceEscapedCharacter(literal);
      }

      return literal;
    },
    options: {
      literal: {
        type: z.any(),
        title: 'Literal',
        description: 'Add a constant value to the template',
        input: Input.TextInput,
        default: undefined,
        conditions: {},
        allowedOptionTypes: [
          OptionType.UserDefined,
          OptionType.Reference,
          OptionType.Graph,
          OptionType.Code,
          OptionType.Boolean,
        ],
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [],
    },
    rules: [BlockRule.Configurable, BlockRule.NoChildren],
  }),
  docVariables: block({
    title: 'Document Variables',
    description: 'Acess a document variable in your template.',
    category: 'core',
    type: 'docVariables',
    node: GraphNode.Generic,
    icon: TbVariable,
    mutation: ({ options }) => {
      const { variable } = options;

      if (reservedWrapVariables.has(variable)) {
        const [mappedObject] = reservedWrapVariables.get(variable)!;
        return mappedObject;
      }

      return variable;
    },
    options: {
      variable: {
        type: z.enum([
          '$emailMessage.text',
          '$emailMessage.from.0.address',
          '$emailMessage.subject',
          '$pdfString',
          '$txtstring',
          '$emailData.fileName',
          '$xlsColumn',
        ]),
        title: 'Variable',
        description: 'Select one of the available document variables',
        input: Input.SingleSelect,
        selection: [
          { value: '$emailMessage.text', label: 'Email Body' },
          { value: '$emailMessage.from.0.address', label: 'Email From' },
          { value: '$emailMessage.subject', label: 'Email Subject' },
          { value: '$pdfString', label: 'PDF Body' },
          { value: '$txtstring', label: 'Text Body' },
          { value: '$emailData.fileName', label: 'File Name' },
          { value: '$xlsColumn', label: 'XLS Column' },
        ],
        default: '',
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [],
    },
    rules: [BlockRule.Configurable, BlockRule.NoChildren],
  }),
  anchorVariable: block({
    title: 'Anchor Variable',
    description: 'Retrieve a field value using a variable path.',
    category: 'core',
    type: 'anchorVariable',
    node: GraphNode.Generic,
    icon: TbVariable,
    mutation: ({ options }) => {
      const { selected } = options;
      if (!selected) return;
      const path = selected.startsWith('$') ? selected.slice(1) : selected;
      return { $getVariable: path };
    },
    options: {
      selected: {
        type: z.string(),
        title: 'Selector',
        description:
          'Select text from the file to use as the variable reference.',
        input: Input.TextSelector,
        default: undefined,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [],
    },
    rules: [BlockRule.Configurable, BlockRule.NoChildren],
  }),
});

const LOGIC_BLOCKS_TRANSFORMER = typeLogicBlockRecord({
  concatenate: block({
    title: 'Concatenate',
    description: 'Combine two or more values into a single string.',
    category: 'transformer',
    type: 'concatenate',
    node: GraphNode.Generic,
    icon: AiOutlineMergeCells,
    mutation: ({ inputs }) => {
      return {
        '$utils.concat.selective': inputs.children,
      };
    },
    options: {},
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'children',
          direction: PortDirection.Input,
          multi: true,
        },
      ],
    },
    rules: [BlockRule.MultipleChildren],
  }),
  split: block({
    title: 'Split',
    description:
      'Split a string, based on a separator, into an array of strings.',
    category: 'transformer',
    type: 'split',
    node: GraphNode.Generic,
    icon: TfiSplitH,
    mutation: ({ inputs, options }) => {
      const { separator, select } = options;

      if (!separator) return;

      return {
        [ReservedNodeType.DeclareVariable]: {
          qa: inputs.child,
          qb: {
            '$qa.split': separator,
            $result: {
              $getVariable: `data.${select}`,
            },
          },
        },
        $getVariable: 'qb',
      };
    },
    options: {
      separator: {
        type: z.string(),
        title: 'Separator',
        description: 'The separator to split the string on.',
        input: Input.TextInput,
        default: undefined,
        conditions: {},
      },
      select: {
        type: z.number(),
        title: 'Select',
        description: 'Select the index of the split string to return.',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.SingleChild],
  }),
  dateTime: block({
    title: 'Date and Time',
    description:
      'Transform a value into a date, transform date formats or output the UNIX time.',
    category: 'transformer',
    type: 'dateTime',
    node: GraphNode.Generic,
    icon: FiCalendar,
    mutation: ({ inputs, options }) => {
      const {
        useCurrentTimestamp,
        useCurrentDate,
        inputType,
        inputFormat,
        outputFormat,
        outputSelection,
        enableRangeCheck,
        minDays,
        maxDays,
        adjustWinterTime,
      } = options;

      // Helper function to convert days to milliseconds
      const daysToUnix = (days: number): number => {
        return days * 24 * 60 * 60 * 1000;
      };

      // Option 1: Use current timestamp - returns immediately
      if (useCurrentTimestamp) {
        return {
          '$time.stamp': [],
        };
      }

      // Option 2: Use current date only - returns immediately
      if (useCurrentDate) {
        return {
          '$time.format': [
            {
              '$time.stamp': ['#$currentTimestamp'],
            },
            'YYYY-MM-DD',
          ],
        };
      }

      // Option 3: Input is epoch time - format directly without $time.stamp
      if (inputType === 'epoch') {
        // If output is unix, just return the input as-is
        if (outputSelection === 'unix') {
          return inputs.child;
        }
        // If output is custom, format the epoch time directly
        if (outputSelection === 'custom' && outputFormat) {
          return {
            '$time.format': [inputs.child, outputFormat],
          };
        }
        // Default: return input as-is if no output format specified
        return inputs.child;
      }

      // Determine the input format
      const selectedInputFormat = inputFormat || undefined;

      // Build the base time stamp operation
      let timeStampOperation: any;

      // If range check is enabled, add validation constraints
      if (enableRangeCheck && minDays !== undefined && maxDays !== undefined) {
        const unixMinDays = daysToUnix(minDays) * -1;
        const unixMaxDays = daysToUnix(maxDays);

        // Range check with optional format
        if (selectedInputFormat) {
          timeStampOperation = {
            '$time.stamp': [
              inputs.child,
              selectedInputFormat,
              {
                gtnow: unixMinDays,
                ltnow: unixMaxDays,
              },
            ],
          };
        } else {
          timeStampOperation = {
            '$time.stamp': [
              inputs.child,
              {
                gtnow: unixMinDays,
                ltnow: unixMaxDays,
              },
            ],
          };
        }
      } else {
        // Standard time stamp with optional format
        if (selectedInputFormat) {
          timeStampOperation = {
            '$time.stamp': [inputs.child, selectedInputFormat],
          };
        } else {
          // No format provided - use tree() directly
          timeStampOperation = {
            '$time.stamp': [inputs.child],
          };
        }
      }

      // Apply winter time adjustment if enabled (wraps the time stamp operation)
      if (adjustWinterTime) {
        const userInputFormat = selectedInputFormat || 'YYYY-MM-DD';
        // Winter time adjustment needs to wrap the base operation
        // If range check was applied, we need to extract the inner operation
        if (
          enableRangeCheck &&
          minDays !== undefined &&
          maxDays !== undefined
        ) {
          // For range check + winter time, wrap the range-checked stamp
          timeStampOperation = {
            '$time.shiftDay': [timeStampOperation, 1],
          };
        } else {
          // Standard winter time adjustment
          timeStampOperation = {
            '$time.shiftDay': [
              {
                '$time.stamp': [inputs.child, userInputFormat],
              },
              1,
            ],
          };
        }
      }

      // Apply output format transformation if custom output is selected (wraps everything)
      if (outputSelection === 'custom' && outputFormat) {
        return {
          '$time.format': [timeStampOperation, outputFormat],
        };
      }

      // Return the base operation (unix timestamp or formatted)
      return timeStampOperation;
    },
    options: {
      useCurrentTimestamp: {
        type: z.boolean(),
        title: 'Use Current Timestamp',
        description: 'When checked, ignores input and uses current timestamp',
        input: Input.Checkbox,
        default: false,
        conditions: {},
      },
      useCurrentDate: {
        type: z.boolean(),
        title: 'Use Current Date',
        description: 'When checked, ignores input and uses current date',
        input: Input.Checkbox,
        default: false,
        conditions: {},
      },
      inputType: {
        type: z.enum(['string', 'epoch']),
        title: 'Input Type',
        description:
          'Specify whether the input is a date string or epoch time (Unix timestamp)',
        input: Input.RadioSelector,
        selection: [
          { value: 'string', label: 'Date String' },
          { value: 'epoch', label: 'Epoch Time' },
        ],
        default: 'string',
        conditions: {},
      },
      inputFormat: {
        type: z.string(),
        title: 'Input Format',
        description:
          'Date and/or time formatting of incoming value. Select from dropdown or enter custom format.',
        input: Input.SingleSelectCreatable,
        selection: [
          { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
          { value: 'YYYY-MMM-DD', label: 'YYYY-MMM-DD' },
          { value: 'YY-MM-DD', label: 'YY-MM-DD' },
          { value: 'DD-MMM-YYYY', label: 'DD-MMM-YYYY' },
          { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY' },
          { value: 'DD-MM-YY', label: 'DD-MM-YY' },
          { value: 'DD-MM', label: 'DD-MM' },
          { value: 'DDD', label: 'DDD' },
          { value: 'YYYY-MM-DD HH:mm', label: 'YYYY-MM-DD HH:mm' },
          { value: 'YYYY-MMM-DD HH:mm', label: 'YYYY-MMM-DD HH:mm' },
          { value: 'YY-MM-DD HH:mm', label: 'YY-MM-DD HH:mm' },
          { value: 'DD-MMM-YYYY HH:mm', label: 'DD-MMM-YYYY HH:mm' },
          { value: 'DD-MM-YYYY HH:mm', label: 'DD-MM-YYYY HH:mm' },
          { value: 'DD-MM-YY HH:mm', label: 'DD-MM-YY HH:mm' },
          { value: 'DD-MM HH:mm', label: 'DD-MM HH:mm' },
          { value: 'DDD HH:mm', label: 'DDD HH:mm' },
        ],
        default: undefined,
        conditions: {
          hasValue: {
            inputType: 'string',
          },
        },
      },
      outputSelection: {
        type: z.enum(['unix', 'custom']),
        title: 'Output Type',
        description: 'Select the date conversion type',
        input: Input.RadioSelector,
        selection: [
          { value: 'unix', label: 'Epoch' },
          { value: 'custom', label: 'Custom' },
        ],
        default: 'unix',
        conditions: {},
      },
      outputFormat: {
        type: z.string(),
        title: 'Output Format',
        description:
          'Date and/or time formatting of outgoing value. Select from dropdown or enter custom format.',
        input: Input.SingleSelectCreatable,
        selection: [
          { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
          { value: 'YYYY-MMM-DD', label: 'YYYY-MMM-DD' },
          { value: 'YY-MM-DD', label: 'YY-MM-DD' },
          { value: 'DD-MMM-YYYY', label: 'DD-MMM-YYYY' },
          { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY' },
          { value: 'DD-MM-YY', label: 'DD-MM-YY' },
          { value: 'DD-MM', label: 'DD-MM' },
          { value: 'DDD', label: 'DDD' },
          { value: 'YYYY-MM-DD HH:mm', label: 'YYYY-MM-DD HH:mm' },
          { value: 'YYYY-MMM-DD HH:mm', label: 'YYYY-MMM-DD HH:mm' },
          { value: 'YY-MM-DD HH:mm', label: 'YY-MM-DD HH:mm' },
          { value: 'DD-MMM-YYYY HH:mm', label: 'DD-MMM-YYYY HH:mm' },
          { value: 'DD-MM-YYYY HH:mm', label: 'DD-MM-YYYY HH:mm' },
          { value: 'DD-MM-YY HH:mm', label: 'DD-MM-YY HH:mm' },
          { value: 'DD-MM HH:mm', label: 'DD-MM HH:mm' },
          { value: 'DDD HH:mm', label: 'DDD HH:mm' },
        ],
        default: undefined,
        conditions: {
          hasValue: {
            outputSelection: 'custom',
          },
        },
      },
      enableRangeCheck: {
        type: z.boolean(),
        title: 'Validate Date Range',
        description:
          'Enable to verify if the date falls between two specific dates',
        input: Input.Checkbox,
        default: false,
        conditions: {},
      },
      minDays: {
        type: z.number(),
        title: 'Min Days',
        description:
          'Check if date is within the last X days (e.g., enter "5" for dates ≥5 days ago)',
        input: Input.TextInput,
        default: undefined,
        conditions: {
          hasValue: {
            enableRangeCheck: true,
          },
        },
      },
      maxDays: {
        type: z.number(),
        title: 'Max Days',
        description:
          'Check if date is within the next X days (e.g., enter "7" for dates ≤7 days in the future)',
        input: Input.TextInput,
        default: undefined,
        conditions: {
          hasValue: {
            enableRangeCheck: true,
          },
        },
      },
      adjustWinterTime: {
        type: z.boolean(),
        title: 'Adjust Winter Time',
        description: 'When checked, adjusts the current date to winter time',
        input: Input.Checkbox,
        default: false,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.SingleChild],
  }),
  substring: block({
    title: 'SubString',
    description: 'Extract a substring from a string.',
    category: 'transformer',
    type: 'substring',
    node: GraphNode.Generic,
    icon: PiTextbox,
    mutation: ({ inputs, options }) => {
      const { from, useEnd, to } = options;

      const renderIndices = () => {
        if (useEnd) return [from, to];
        return from;
      };

      return {
        [ReservedNodeType.DeclareVariable]: {
          varToParse: inputs.child,
        },
        '$varToParse.substring': renderIndices(),
      };
    },
    options: {
      from: {
        type: z.number(),
        title: 'From',
        description: 'The starting index of the substring',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
      useEnd: {
        type: z.boolean(),
        title: 'Use End',
        description: 'Use an index to define the end of the substring',
        input: Input.Checkbox,
        default: true,
        conditions: {},
      },
      to: {
        type: z.number(),
        title: 'To',
        description: 'The ending index of the substring',
        input: Input.Number,
        default: 0,
        conditions: {
          hasValue: {
            useEnd: true,
          },
        },
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.SingleChild],
  }),
  removeWords: block({
    title: 'Remove Words',
    description: 'Remove words from a string.',
    category: 'transformer',
    type: 'removeWords',
    node: GraphNode.Generic,
    icon: LuEraser,
    mutation: ({ inputs, options }) => {
      const { countStart, countEnd } = options;
      return {
        '$helpers.removeWords': [[inputs.children], countStart, countEnd],
      };
    },
    options: {
      countStart: {
        type: z.number(),
        title: 'Starting count',
        description:
          'The amount of words to remove from the start of the string.',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
      countEnd: {
        type: z.number(),
        title: 'Ending count',
        description:
          'The amount of words to remove from the end of the string.',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'children',
          direction: PortDirection.Input,
          multi: true,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.MultipleChildren],
  }),
  removeSpecialCharacters: block({
    title: 'Remove Special Characters',
    description:
      'Remove special characters and (trailing) whitespace from a string.',
    category: 'transformer',
    type: 'removeSpecialCharacters',
    node: GraphNode.Generic,
    icon: LuEraser,
    mutation: ({ inputs, options }) => {
      const { trimWhitespace } = options;

      const renderTrim = (el: any) => {
        if (trimWhitespace) return [[el]];
        return el;
      };

      return {
        '$utils.regexp.replace': [
          renderTrim(inputs.child),
          ['[\n,\r, ,/,(,)]', 'g'],
          '',
        ],
      };
    },
    options: {
      trimWhitespace: {
        type: z.boolean(),
        title: 'Trim Whitespace',
        description: 'Remove trailing whitespace from the string',
        input: Input.Checkbox,
        default: true,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.SingleChild],
  }),
  removeLeadingAndTrailingSpaces: block({
    title: 'Remove Leading and Trailing Spaces',
    description: 'Remove spaces at begin and end of a string.',
    category: 'transformer',
    type: 'removeLeadingAndTrailingSpaces',
    node: GraphNode.Generic,
    icon: LuEraser,
    mutation: ({ inputs }) => {
      return [[inputs.child]];
    },
    options: {},
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.SingleChild],
  }),
  replaceSymbols: block({
    title: 'Replace symbols',
    description: 'Replace symbols of 1 or more characters in a string',
    category: 'transformer',
    type: 'replaceSymbols',
    node: GraphNode.Generic,
    icon: LuReplaceAll,
    mutation: ({ inputs, options }) => {
      const { symbols, target, global, caseInsensitive } = options;

      if ((!target || !symbols) && target != '') return;

      const renderFlags = () => {
        return `${global ? 'g' : ''}${caseInsensitive ? 'i' : ''}`;
      };

      const renderRegExp = (symbols: string | undefined) => {
        const flags = renderFlags();

        if (!flags && symbols === undefined) return [];
        if (!flags) return [symbols];
        if (symbols === undefined) return ['', flags];

        return [symbols, flags];
      };

      const singleCharSymbols = () => {
        return symbols.filter((symbol) => symbol.length === 1);
      };

      const multiCharSymbols = () => {
        return symbols.filter((symbol) => symbol.length > 1);
      };

      const renderSingleCharSymbols = () => {
        if (singleCharSymbols().length === 0) return inputs.child;
        const symbolsArray = `[${singleCharSymbols().join(',')}]`;
        return {
          '$utils.regexp.replace': [
            inputs.child,
            renderRegExp(symbolsArray),
            target,
          ],
        };
      };

      const renderRecursive = (symbols: string[]): any => {
        if (symbols.length === 0) return renderSingleCharSymbols();
        return {
          '$utils.regexp.replace': [
            renderRecursive(symbols.slice(1)),
            renderRegExp(symbols[0]),
            target,
          ],
        };
      };

      return renderRecursive(multiCharSymbols());
    },
    options: {
      symbols: {
        type: z.array(z.string()),
        title: 'Symbols',
        description: 'The symbols to replace within the incoming string.',
        input: Input.MultiSelectCreatable,
        default: [],
        conditions: {},
      },
      target: {
        type: z.string(),
        title: 'Target',
        description: 'The target value to replace the symbols with.',
        input: Input.SingleSelectCreatable,
        selection: [
          { value: '', label: 'Empty' },
          { value: '\n', label: 'Newline' },
        ],
        default: undefined,
        conditions: {},
      },
      global: {
        type: z.boolean(),
        title: 'Global',
        description: 'Match all occurrences of the pattern',
        input: Input.Checkbox,
        default: false,
        conditions: {},
      },
      caseInsensitive: {
        type: z.boolean(),
        title: 'Case Insensitive',
        description: 'Ignore case when matching',
        input: Input.Checkbox,
        default: false,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.SingleChild],
  }),
  parseToNumber: block({
    title: 'Parse to Number',
    description: 'Parse a string to a number.',
    category: 'transformer',
    type: 'parseToNumber',
    node: GraphNode.Generic,
    icon: TbNumbers,
    mutation: ({ inputs }) => {
      return {
        '$math.parseInt': inputs.child,
      };
    },
    options: {},
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.SingleChild],
  }),
  parseToFloat: block({
    title: 'Parse to Broken Number',
    description: 'Parse a string to a broken number (float).',
    category: 'transformer',
    type: 'parseToFloat',
    node: GraphNode.Generic,
    icon: TbNumbers,
    mutation: ({ inputs }) => {
      return {
        '$math.parseFloat': inputs.child,
      };
    },
    options: {},
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.SingleChild],
  }),
  parseToFloatReversed: block({
    title: 'Parse to Broken Number Reversed',
    description: 'Parse a string to a broken number (float). (Reversed)',
    category: 'transformer',
    type: 'parseToFloatReversed',
    node: GraphNode.Generic,
    icon: TbNumbers,
    mutation: ({ inputs }) => {
      return {
        '$helpers.weight': inputs.child,
      };
    },
    options: {},
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.SingleChild],
  }),
});

const LOGIC_BLOCKS_LOGIC = typeLogicBlockRecord({
  logicOr: block({
    title: 'OR',
    description: 'Combine two or more values with an OR operator.',
    category: 'logic',
    type: 'logicOr',
    node: GraphNode.Generic,
    icon: TbLogicOr,
    mutation: ({ inputs }) => {
      return {
        '$utils.or': inputs.children,
      };
    },
    options: {},
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'children',
          direction: PortDirection.Input,
          multi: true,
        },
      ],
    },
    rules: [BlockRule.MultipleChildren],
  }),
  logicAnd: block({
    title: 'AND',
    description: 'Combine two or more values with an AND operator.',
    category: 'logic',
    type: 'logicAnd',
    node: GraphNode.Generic,
    icon: TbLogicAnd,
    mutation: ({ inputs }) => {
      return {
        '$utils.and': inputs.children,
      };
    },
    options: {},
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'children',
          direction: PortDirection.Input,
          multi: true,
        },
      ],
    },
    rules: [BlockRule.MultipleChildren],
  }),
  logicEqual: block({
    title: 'Equal',
    description: 'Check if two values are equal.',
    category: 'logic',
    type: 'logicEqual',
    node: GraphNode.Generic,
    icon: TbEqual,
    mutation: ({ inputs, options }) => {
      const { invert } = options;

      if (invert) {
        return {
          '$utils.neq': inputs.children,
        };
      }

      return {
        '$utils.eq': inputs.children,
      };
    },
    options: {
      invert: {
        type: z.boolean(),
        title: 'Invert',
        description: 'Invert the equality check. (Check for inequality)',
        input: Input.Checkbox,
        default: false,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'children',
          direction: PortDirection.Input,
          multi: true,
        },
      ],
    },
    rules: [BlockRule.MultipleChildren, BlockRule.Configurable],
  }),
  logicNot: block({
    title: 'Not',
    description: 'Invert a boolean value.',
    category: 'logic',
    type: 'logicNot',
    node: GraphNode.Generic,
    icon: TbExclamationMark,
    mutation: ({ inputs }) => {
      return {
        '$utils.not': inputs.child,
      };
    },
    options: {},
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.SingleChild],
  }),
  logicRegExpTest: block({
    title: 'Regular Expression Test',
    description: 'Check if a value matches a regular expression.',
    category: 'logic',
    type: 'logicRegExpTest',
    node: GraphNode.Generic,
    icon: TbMathFunction,
    mutation: ({ inputs, options }) => {
      const { pattern, global, caseInsensitive } = options;

      if (!pattern) return;

      const renderFlags = () => {
        return `${global ? 'g' : ''}${caseInsensitive ? 'i' : ''}`;
      };

      const renderRegExp = () => {
        const flags = renderFlags();
        if (!flags) return [pattern];

        return [pattern, flags];
      };

      const treeString = JSON.stringify(inputs.child);

      const getWrapVariableForTree = () => {
        for (const [
          mappedObject,
          wrapVariable,
        ] of reservedWrapVariables.values()) {
          if (JSON.stringify(mappedObject) === treeString) {
            return wrapVariable;
          }
        }

        return null;
      };

      const wrapVariable = getWrapVariableForTree();

      // Return the wrapped construct if the inputs['1'] is a mapped variable in reservedWrapVariables
      if (wrapVariable) {
        return {
          [wrapVariable]: {
            '$utils.regexp.test': [inputs.child, renderRegExp()],
          },
        };
      }

      return {
        '$utils.regexp.test': [inputs.child, renderRegExp()],
      };
    },
    options: {
      pattern: {
        type: z.string(),
        title: 'Pattern',
        description: 'The regular expression pattern',
        input: Input.TextInput,
        default: undefined,
        conditions: {},
      },
      global: {
        type: z.boolean(),
        title: 'Global',
        description: 'Match all occurrences of the pattern',
        input: Input.Checkbox,
        default: false,
        conditions: {},
      },
      caseInsensitive: {
        type: z.boolean(),
        title: 'Case Insensitive',
        description: 'Ignore case when matching',
        input: Input.Checkbox,
        default: false,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.SingleChild],
  }),
  logicIfElse: block({
    title: 'Condition',
    description: 'Forward a value based on a condition.',
    category: 'logic',
    type: 'logicIfElse',
    node: GraphNode.Generic,
    icon: LuSplit,
    mutation: ({ inputs }) => {
      const { condition, then, otherwise } = inputs;

      if (!condition) {
        return null;
      }

      return {
        $if: condition,
        $then: then ?? null,
        $else: otherwise ?? null,
      };
    },
    options: {},
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'condition',
          direction: PortDirection.Input,
          label: 'if',
        },
        {
          id: 'then',
          direction: PortDirection.Input,
          label: 'then',
        },
        {
          id: 'otherwise',
          direction: PortDirection.Input,
          label: 'else',
        },
      ],
    },
    rules: [BlockRule.Configurable],
  }),
  switchCase: block({
    title: 'Switch Case',
    description: 'Evaluate multiple conditions using a switch-case structure.',
    category: 'logic',
    type: 'switchCase',
    node: GraphNode.Generic,
    icon: LuGitBranch,
    mutation: ({ inputs }) => {
      const { expression, cases, default: defaultCase } = inputs;

      if (!expression || !(cases as unknown[]).length) return;

      return {
        $switch: expression,
        $case: (cases as unknown[]).flat(),
        ...(defaultCase !== undefined && { $default: defaultCase }),
      };
    },
    options: {},
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'expression',
          direction: PortDirection.Input,
          label: 'condition',
        },
        {
          id: 'cases',
          direction: PortDirection.Input,
          label: 'cases',
          multi: true,
        },
        {
          id: 'default',
          direction: PortDirection.Input,
          label: 'default',
        },
      ],
    },
    rules: [BlockRule.Configurable],
  }),
});

const LOGIC_BLOCKS_MATH = typeLogicBlockRecord({
  mathPlus: block({
    title: 'Plus',
    description: 'Add two or more values together.',
    category: 'math',
    type: 'mathPlus',
    node: GraphNode.Generic,
    icon: TbPlus,
    mutation: ({ inputs }) => {
      return {
        '$math.sum': inputs.children,
      };
    },
    options: {},
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'children',
          direction: PortDirection.Input,
          multi: true,
        },
      ],
    },
    rules: [BlockRule.MultipleChildren],
  }),
  mathSubtract: block({
    title: 'Subtract',
    description: 'Subtract one value from the other',
    category: 'math',
    type: 'mathSubtract',
    node: GraphNode.Generic,
    icon: TbMinus,
    mutation: ({ inputs, options }) => {
      const { subtract } = options;

      return {
        '$math.minus': [inputs.child, subtract],
      };
    },
    options: {
      subtract: {
        type: z.number(),
        title: 'Subtract',
        description: 'Subtract this value from the incoming value',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.SingleChild],
  }),
  mathMultiply: block({
    title: 'Multiply',
    description: 'Multiply two or more values together.',
    category: 'math',
    type: 'mathMultiply',
    node: GraphNode.Generic,
    icon: TbX,
    mutation: ({ inputs }) => {
      return {
        '$math.multiply': inputs.children,
      };
    },
    options: {},
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'children',
          direction: PortDirection.Input,
          multi: true,
        },
      ],
    },
    rules: [BlockRule.MultipleChildren],
  }),
  mathRound: block({
    title: 'Round',
    description: 'Round a value to the nearest integer.',
    category: 'math',
    type: 'mathRound',
    node: GraphNode.Generic,
    icon: TbDecimal,
    mutation: ({ inputs }) => {
      return {
        '$math.round': inputs.child,
      };
    },
    options: {},
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.SingleChild],
  }),
  mathCeil: block({
    title: 'Ceil',
    description: 'Round up a value to the nearest integer.',
    category: 'math',
    type: 'mathCeil',
    node: GraphNode.Generic,
    icon: TbBracketsContainEnd,
    mutation: ({ inputs }) => {
      return {
        '$math.ceil': inputs.child,
      };
    },
    options: {},
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.SingleChild],
  }),
  mathFloor: block({
    title: 'Floor',
    description: 'Round down a value to the nearest integer.',
    category: 'math',
    type: 'mathFloor',
    node: GraphNode.Generic,
    icon: TbBracketsContainStart,
    mutation: ({ inputs }) => {
      return {
        '$math.floor': inputs.child,
      };
    },
    options: {},
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.SingleChild],
  }),
  mathDivide: block({
    title: 'Divide',
    description: 'Divide one value by the other.',
    category: 'math',
    type: 'mathDivide',
    node: GraphNode.Generic,
    icon: CgMathDivide,
    mutation: ({ inputs, options }) => {
      const { divisor } = options;

      return {
        '$math.divide': [inputs.child, divisor],
      };
    },
    options: {
      divisor: {
        type: z.number(),
        title: 'Divisor',
        description: 'Divide the incoming value by this value',
        input: Input.Number,
        default: 1,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.SingleChild],
  }),
  mathMaximal: block({
    title: 'Maximal',
    description: 'Get the maximal value of two or more values.',
    category: 'math',
    type: 'mathMaximal',
    node: GraphNode.Generic,
    icon: PiArrowUp,
    mutation: ({ inputs }) => {
      return {
        '$math.max': inputs.children,
      };
    },
    options: {},
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'children',
          direction: PortDirection.Input,
          multi: true,
        },
      ],
    },
    rules: [BlockRule.MultipleChildren],
  }),
  mathLessThan: block({
    title: 'Less Than',
    description: 'Check if a value is less than another value.',
    category: 'math',
    type: 'mathLessThan',
    node: GraphNode.Generic,
    icon: TbMathLower,
    mutation: ({ inputs, options }) => {
      const { lessThan } = options;

      return {
        '$math.lt': [inputs.child, lessThan],
      };
    },
    options: {
      lessThan: {
        type: z.number(),
        title: 'Less Than',
        description: 'Check if the incoming value is less than this value',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.SingleChild],
  }),
  mathLessThanOrEqual: block({
    title: 'Less Than or Equal',
    description: 'Check if a value is less than or equal to another value.',
    category: 'math',
    type: 'mathLessThanOrEqual',
    node: GraphNode.Generic,
    icon: TbMathEqualLower,
    mutation: ({ inputs, options }) => {
      const { lessThanOrEqual } = options;

      return {
        '$math.lte': [inputs.child, lessThanOrEqual],
      };
    },
    options: {
      lessThanOrEqual: {
        type: z.number(),
        title: 'Less Than or Equal',
        description:
          'Check if the incoming value is less than or equal to this value',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.SingleChild],
  }),
  mathGreaterThan: block({
    title: 'Greater Than',
    description: 'Check if a value is greater than another value.',
    category: 'math',
    type: 'mathGreaterThan',
    node: GraphNode.Generic,
    icon: TbMathGreater,
    mutation: ({ inputs, options }) => {
      const { greaterThan } = options;

      return {
        '$math.gt': [inputs.child, greaterThan],
      };
    },
    options: {
      greaterThan: {
        type: z.number(),
        title: 'Greater Than',
        description: 'Check if the incoming value is greater than this value',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.SingleChild],
  }),
  mathGreaterThanOrEqual: block({
    title: 'Greater Than or Equal',
    description: 'Check if a value is greater than or equal to another value.',
    category: 'math',
    type: 'mathGreaterThanOrEqual',
    node: GraphNode.Generic,
    icon: TbMathEqualGreater,
    mutation: ({ inputs, options }) => {
      const { greaterThanOrEqual } = options;

      return {
        '$math.gte': [inputs.child, greaterThanOrEqual],
      };
    },
    options: {
      greaterThanOrEqual: {
        type: z.number(),
        title: 'Greater Than or Equal',
        description:
          'Check if the incoming value is greater than or equal to this value',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [
        {
          id: 'child',
          direction: PortDirection.Input,
        },
      ],
    },
    rules: [BlockRule.Configurable, BlockRule.SingleChild],
  }),
});

const LOGIC_BLOCKS_REQUEST = typeLogicBlockRecord({
  genericRequest: block({
    title: 'Generic Request',
    description:
      'Make a request to a database, use this component to implement custom logic.',
    category: 'request',
    type: 'genericRequest',
    node: GraphNode.Generic,
    icon: DiMongodb,
    mutation: ({ options }) => {
      // const { database, collection, parameters, target } = options;
      const { database, collection, target } = options;

      if (!database || !collection || !target) return;

      return {
        $mongoRequest: [
          {
            [`$db.${collection}.findOne`]: {
              // ...parameters,
            },
            $result: {
              $getVariable: target,
            },
          },
          database,
          [collection],
        ],
      };
    },
    options: {
      database: {
        type: z.string(),
        title: 'Database',
        description: 'The database which to fetch data from',
        input: Input.TextInput,
        default: undefined,
        conditions: {},
      },
      collection: {
        type: z.string(),
        title: 'Collection',
        description: 'The collection which to fetch data from',
        input: Input.TextInput,
        default: undefined,
        conditions: {},
      },
      target: {
        type: z.string(),
        title: 'Target',
        description: 'The target value to fetch from the resulting data',
        input: Input.TextInput,
        default: undefined,
        conditions: {},
      },
      // FIXME
      // parameters: {
      //   type: z.any(),
      //   title: 'Parameters',
      //   description: 'Define values which are passed to the database operator',
      //   // FIXME: input: Input.ParameterTable,
      //   input: Input.ParameterTable,
      //   default: {},
      //   conditions: {
      //     isFullWidth: true,
      //   },
      // },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [],
    },
    rules: [BlockRule.Configurable, BlockRule.NoChildren],
  }),
  vesselRequest: block({
    title: 'Vessel Request',
    description: 'Fetch vessel information from a database.',
    category: 'request',
    type: 'vesselRequest',
    node: GraphNode.Generic,
    icon: DiMongodb,
    mutation: ({ options }) => {
      // const { database, collection, parameters, target } = options;
      const { database, collection, target } = options;

      return {
        $mongoRequest: [
          {
            [`$db.${collection}.findOne`]: {
              // ...parameters,
            },
            $result: {
              $getVariable: target,
            },
          },
          database,
          [collection],
        ],
      };
    },
    options: {
      database: {
        type: z.literal('morphjs'),
        title: 'Database',
        description: 'The database which to fetch data from',
        input: Input.Literal,
        default: 'morphjs',
        conditions: {
          isStatic: true,
        },
      },
      collection: {
        type: z.literal('resource'),
        title: 'Collection',
        description: 'The collection which to fetch data from',
        input: Input.Literal,
        default: 'resource',
        conditions: {
          isStatic: true,
        },
      },
      target: {
        type: z.literal('CODE'),
        title: 'Target',
        description: 'The target value to fetch from the resulting data',
        input: Input.Literal,
        default: 'CODE',
        conditions: {
          isStatic: true,
        },
      },
      // FIXME: parameters had a malfunctioning UI implementation that was also based on old framework code.
      // To fix this properly implement a new UI element that can be rendered correctly in the node configuration panel.
      // parameters: {
      //   type: z
      //     .object({
      //       operatorid: z.number(),
      //       alias: z.string(),
      //     })
      //     .required()
      //     .and(z.record(z.any())),
      //   title: 'Parameters',
      //   description: 'Define values which are passed to the database operator',
      //   // FIXME: parameters: Input.ParameterTable,
      //   input: Input.ParameterTable,
      //   default: {
      //     operatorid: {
      //       type: OptionType.Dependency,
      //       value: undefined,
      //     },
      //     alias: {
      //       type: OptionType.Dependency,
      //       value: undefined,
      //     },
      //   },
      //   conditions: {
      //     isFullWidth: true,
      //   },
      // },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [],
    },
    rules: [BlockRule.Configurable, BlockRule.NoChildren],
  }),
});

const LOGIC_BLOCKS_TABLE = typeLogicBlockRecord({
  composeTableColumns: block({
    title: 'Compose Table',
    description: 'Construct a table by automatically identifying the columns.',
    category: 'table',
    type: 'composeTableColumns',
    node: GraphNode.Generic,
    icon: TbTable,
    mutation: ({ options }) => {
      const { definitionInHeader, ...restOptions } = options;

      if (restOptions.from === undefined) {
        return;
      }

      if (definitionInHeader) {
        return {
          '#$composeTableColumns': restOptions,
        };
      }

      // If definitionInHeader is false, exclude 'fromLine'
      const { fromLine, ...filteredOptions } = restOptions;
      return {
        '#$composeTableColumns': filteredOptions,
      };
    },
    options: {
      from: {
        type: z.string(),
        title: 'From',
        description: 'Select the variable to compose',
        input: Input.TextSelector,
        default: undefined,
        conditions: {},
      },
      definitionInHeader: {
        type: z.boolean(),
        title: 'Table Definition in Header',
        description: 'Select if table definition starts from the header',
        input: Input.Checkbox,
        default: true,
        conditions: { isStatic: true },
      },
      lines: {
        type: z.number(),
        title: 'Lines',
        description:
          'The number of lines to skip from the marker before starting to compose the table',
        input: Input.Number,
        default: 1,
        conditions: {
          hasValue: {
            definitionInHeader: false,
          },
        },
      },
      fromLine: {
        type: z.number(),
        title: 'From Line',
        description: 'The starting point for column headers in the table',
        input: Input.Number,
        default: 0,
        conditions: { isHidden: true },
      },
      compose: {
        type: z.boolean(),
        title: 'Compose',
        description: 'Enable different compose algorithm to compose the table',
        input: Input.Checkbox,
        default: false,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [],
    },
    rules: [BlockRule.Configurable, BlockRule.NoChildren],
  }),
  defineTableCell: block({
    title: 'Cell',
    description: 'Get information from a cell within a table.',
    category: 'table',
    type: 'defineTableCell',
    node: GraphNode.Generic,
    icon: TbTableAlias,
    mutation: ({ options }) => {
      const { shiftRows, shiftColumns, iterate, ...config } = options;

      const renderIterate = () => {
        if (iterate) return '>';
        return '';
      };

      return {
        [`#${renderIterate()}${shiftRows},${shiftColumns}$cell#`]: config,
      };
    },
    options: {
      shiftRows: {
        type: z.number(),
        title: 'Target Row',
        description: 'The row number of the cell',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
      shiftColumns: {
        type: z.number(),
        title: 'Target Column',
        description: 'The column number of the cell',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
      iterate: {
        type: z.boolean(),
        title: 'Iterate',
        description: 'Iterate over the cell',
        input: Input.Checkbox,
        default: false,
        conditions: {},
      },
      from: {
        type: z.string(),
        title: 'From',
        description: 'The from marker used in composing the table',
        input: Input.TextSelector,
        default: undefined,
        conditions: {},
      },
      fromLine: {
        type: z.number(),
        title: 'From Line',
        description: 'The line used in composing the table',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
      columns: {
        type: z.any(),
        title: 'Columns',
        description: 'Column definitions used to index the cells of the table',
        input: Input.TextInput,
        default: undefined,
        conditions: {
          isConnector: true,
        },
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [],
    },
    rules: [BlockRule.Configurable, BlockRule.NoChildren],
  }),
  iterateRowByColumn: block({
    title: 'Column',
    description: 'Iterate over a column within a table.',
    category: 'table',
    type: 'iterateRowByColumn',
    node: GraphNode.Generic,
    icon: TbColumns3,
    mutation: ({ options }) => {
      const { column } = options;

      return `#>$column#${column}`;
    },
    options: {
      column: {
        type: z.number(),
        title: 'Column',
        description: 'Select target column to iterate over',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [],
    },
    rules: [BlockRule.Configurable, BlockRule.NoChildren],
  }),
});

const LOGIC_BLOCKS_ITERATOR = typeLogicBlockRecord({
  countOccurences: block({
    title: 'Count Occurrences',
    description:
      'Iterate over a value which appears multiple times, used to relatively reference fields to extract.',
    category: 'iterator',
    type: 'countOccurences',
    node: GraphNode.Generic,
    icon: TbBoxMultiple,
    mutation: ({ options, ctx }) => {
      const { countoccurences, referBack } = options;
      const { isRoot } = ctx;

      const parseCountOccurences = (value: string[]) => {
        if (value.length === 1) return value[0];
        return value;
      };

      const parsedCountOccurences = countoccurences
        ? parseCountOccurences(countoccurences)
        : undefined;

      if (!parsedCountOccurences && !referBack) {
        return;
      }

      if (!referBack) {
        if (isRoot) {
          return { countoccurences: parsedCountOccurences };
        }

        return {
          [ReservedNodeType.Iterator]: {
            countoccurences: parsedCountOccurences,
          },
        };
      }

      if (isRoot) {
        return {
          countoccurences: parsedCountOccurences,
          referBack: replaceEscapedCharacter(referBack),
        };
      }

      return {
        [ReservedNodeType.Iterator]: {
          countoccurences: parsedCountOccurences,
          referBack: replaceEscapedCharacter(referBack),
        },
      };
    },
    options: {
      countoccurences: {
        type: z.array(z.string()),
        title: 'Count Occurences',
        description: 'Select text from file which will be used to iterate over',
        input: Input.TextSelectorAdd,
        default: undefined,
        conditions: {},
      },
      referBack: {
        type: z.string(),
        title: 'Refer Back',
        description:
          'Specify the starting point of the occurrence, either from the beginning of the line or a specific character/word before the keyword',
        input: Input.TextInput,
        default: undefined,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [],
    },
    rules: [BlockRule.Configurable, BlockRule.NoChildren],
  }),
  iterableTable: block({
    title: 'Iterate Table',
    description: 'Iterate over a table.',
    category: 'iterator',
    type: 'iterableTable',
    node: GraphNode.Generic,
    icon: TbTable,
    mutation: ({ options, ctx }) => {
      const { from, to, findByColumns, columns, skip, fromLine, shiftRow } =
        options;
      const { isRoot } = ctx;

      if (isRoot) {
        return {
          table: {
            from,
            to,
            findByColumns,
            columns,
            skip,
            fromLine,
            shiftRow,
          },
        };
      }

      return {
        [ReservedNodeType.Iterator]: {
          table: {
            from,
            to,
            findByColumns,
            columns,
            skip,
            fromLine,
            shiftRow,
          },
        },
      };
    },
    options: {
      from: {
        type: z.string(),
        title: 'From Marker',
        description: 'The marker in the file to start composing the table from',
        input: Input.TextSelector,
        default: undefined,
        conditions: {},
      },
      to: {
        type: z.string(),
        title: 'To Marker',
        description: 'The marker in the file where to end composing the table',
        input: Input.TextSelector,
        default: undefined,
        conditions: {},
      },
      fromLine: {
        type: z.number(),
        title: 'From Line',
        description:
          'The number of lines to skip from the marker before starting to compose the table',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
      shiftRow: {
        type: z.number(),
        title: 'Shift Row',
        description:
          'The number of rows to skip from the start of the table before iterating over values',
        input: Input.Number,
        default: 0,
        conditions: {},
      },
      findByColumns: {
        type: z.boolean(),
        title: 'Find by Columns',
        description: 'Enable different compose algorithm to compose the table',
        input: Input.Checkbox,
        default: false,
        conditions: {},
      },
      columns: {
        type: z.any(),
        title: 'Columns',
        description: 'Column definitions used to index the cells of the table',
        input: Input.TextInput,
        default: undefined,
        conditions: {
          isConnector: true,
        },
      },
      skip: {
        type: z.any(),
        title: 'Skip',
        description: 'Define which rows to skip from the table',
        input: Input.TextInput,
        default: undefined,
        conditions: {
          isConnector: true,
        },
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [],
    },
    rules: [BlockRule.Configurable, BlockRule.NoChildren],
  }),
  getFromArray: block({
    title: 'Get From List',
    description: 'Get element(s) from a list.',
    category: 'iterator',
    type: 'getFromArray',
    node: GraphNode.Generic,
    icon: MdDataArray,
    mutation: ({ options }) => {
      const { value, reference, skip, returnMode, targetElement } = options;

      const renderSkip = () => {
        if (!skip) return {};
        return { $skip: filterIncomingPathReference(skip, reference) };
      };

      const extractElement = () => {
        if (returnMode === 'all') return 'data';
        if (targetElement === undefined) return 'data';
        return 'data.' + targetElement;
      };

      if (!value || !reference) {
        return;
      }

      return {
        '$Array.forEach': filterIncomingPathReference(value, reference),
        $data: reference,
        ...renderSkip(),
        $result: {
          $getVariable: extractElement(),
        },
      };
    },
    options: {
      value: {
        type: z.any(),
        title: 'Value',
        description: 'The value to extract from the list',
        input: Input.TextSelector,
        default: undefined,
        conditions: {},
      },
      reference: {
        type: z.string(),
        title: 'Reference',
        description: 'The reference to the list to iterate over',
        input: Input.TextSelector,
        default: undefined,
        conditions: {},
      },
      skip: {
        type: z.boolean(),
        title: 'Skip',
        description: 'Determine if the current element should be skipped.',
        input: Input.TextInput,
        default: undefined,
        conditions: {
          isConnector: true,
        },
      },
      returnMode: {
        type: z.enum(['all', 'single']),
        title: 'Return Mode',
        description:
          'Select whether to return the entire list or a single element from the list.',
        input: Input.RadioSelector,
        selection: [
          { value: 'all', label: 'Entire List' },
          { value: 'single', label: 'Single Element' },
        ],
        default: 'all',
        conditions: {},
      },
      targetElement: {
        type: z.number(),
        title: 'Target Element',
        description: 'The target element to extract from the list',
        input: Input.Number,
        default: 0,
        conditions: {
          hasValue: {
            returnMode: 'single',
          },
        },
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [],
    },
    rules: [BlockRule.Configurable, BlockRule.NoChildren],
  }),
  sumFromArray: block({
    title: 'Sum From List',
    description: 'Sum element(s) from a list.',
    category: 'iterator',
    type: 'sumFromArray',
    node: GraphNode.Generic,
    icon: MdDataArray,
    mutation: ({ options }) => {
      const { value, reference } = options;

      if (!value && !reference) return;

      return {
        '$Array.forEach': {
          '$math.parseFloat': filterIncomingPathReference(value, reference),
        },
        $data: reference,
        $result: {
          '$math.sum': '$data',
        },
      };
    },
    options: {
      value: {
        type: z.any(),
        title: 'Value',
        description: 'The value to extract from the list',
        input: Input.TextSelector,
        default: undefined,
        conditions: {},
      },
      reference: {
        type: z.string(),
        title: 'Reference',
        description: 'The reference to the list to iterate over',
        input: Input.TextSelector,
        default: undefined,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [],
    },
    rules: [BlockRule.Configurable, BlockRule.NoChildren],
  }),
  containerIterator: block({
    title: 'Container Iterator',
    description: 'Define the quantity of containers to iterate over.',
    category: 'iterator',
    type: 'containerIterator',
    node: GraphNode.Generic,
    icon: TbTable,
    mutation: ({ options, ctx }) => {
      const { quantity, declareVariableIterator } = options;
      const { isRoot } = ctx;

      if (!quantity) return;

      const content = {
        template: {
          _containerid: {
            '$math.plus': ['$ITERATOR', 1],
          },
        },
        data: {
          [ReservedNodeType.RepeatItems]: [
            {
              repeat: {
                [ReservedNodeType.ParseInt]: quantity,
              },
              _containerid: '$CURRENT_COUNTER',
              $setVariable: {
                repetitionc: {
                  [ReservedNodeType.ParseInt]: quantity,
                },
              },
            },
          ],
        },
      };

      if (declareVariableIterator) {
        if (isRoot) {
          return {
            [ReservedNodeType.DeclareVariable]: {
              [declareVariableIterator]: content,
            },
          };
        }
        return content;
      }

      return content;
    },
    options: {
      quantity: {
        type: z.string(),
        title: 'Quantity',
        description: 'Number of containers that is defined in the file.',
        input: Input.TextSelector,
        default: undefined,
        conditions: {},
      },
      declareVariableIterator: {
        type: z.string(),
        title: 'Variable Declaration',
        description: 'This container iterator is a self defined variable.',
        input: Input.TextInput,
        default: undefined,
        conditions: {},
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [],
    },
    rules: [BlockRule.Configurable, BlockRule.NoChildren],
  }),
});

const LOGIC_BLOCKS_CASE = typeLogicBlockRecord({
  caseDefinition: block({
    title: 'Case Definition',
    description: 'Specify conditions within a switch-case structure.',
    category: 'case',
    type: 'caseDefinition',
    node: GraphNode.Generic,
    icon: TbBoxMultiple,
    mutation: ({ options }) => {
      const { variableKey, variableValue } = options;

      if (!variableKey || variableValue === undefined) return;

      return [variableKey, variableValue];
    },
    options: {
      variableKey: {
        type: z.string(),
        title: 'Match',
        description: 'The value to compare against the condition.',
        input: Input.TextInput,
        default: undefined,
        conditions: { isStatic: true },
      },
      variableValue: {
        type: z.string(),
        title: 'Result',
        description: 'The value to return when the condition matches.',
        input: Input.TextInput,
        default: undefined,
        conditions: {},
        allowedOptionTypes: [
          OptionType.UserDefined,
          OptionType.Reference,
          OptionType.Graph,
          OptionType.Code,
          OptionType.Boolean,
        ],
      },
    },
    ports: {
      output: [
        {
          id: '1',
          direction: PortDirection.Output,
        },
      ],
      input: [],
    },
    rules: [
      BlockRule.Configurable,
      BlockRule.NoChildren,
      BlockRule.MultipleElement,
    ],
  }),
});

export const LOGIC_BLOCKS = {
  ...LOGIC_BLOCKS_INTERNAL,
  ...LOGIC_BLOCKS_CORE,
  ...LOGIC_BLOCKS_TRANSFORMER,
  ...LOGIC_BLOCKS_LOGIC,
  ...LOGIC_BLOCKS_MATH,
  ...LOGIC_BLOCKS_REQUEST,
  ...LOGIC_BLOCKS_TABLE,
  ...LOGIC_BLOCKS_ITERATOR,
  ...LOGIC_BLOCKS_CASE,
};
